// Drive left menu — folder tree (scope → root folders → sub-folders).
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../shared/api/client";
import { I } from "../shared/Icon";
import { toast } from "../shared/stores/toasts";
import { useAuth } from "../shared/stores/auth";
import { ContextMenu, type CtxItem } from "./SubNav";

type DriveFolder = { id: number; name: string; scope: string; parent_id: number | null; sort_order: number };

const SCOPES = [
  { id: "company",    label: "전사",  api: "COMPANY" },
  { id: "department", label: "부서",  api: "DEPARTMENT" },
  { id: "personal",   label: "개인",  api: "PERSONAL" },
] as const;
type ScopeId = typeof SCOPES[number]["id"];

// ── Section divider helpers ─────────────────────────────────────────────────────

const SECTION_MARK = "❖";
const isDivider = (f: DriveFolder) => f.name.startsWith(SECTION_MARK);

function parseSection(name: string): { text: string; uid: string | null } {
  const m = name.match(/^❖([\s\S]*)❖(\d+)$/);
  if (m) return { text: m[1], uid: m[2] };
  if (name.startsWith(SECTION_MARK)) return { text: name.slice(SECTION_MARK.length), uid: null };
  return { text: "", uid: null };
}

function buildSectionName(text: string, uid?: string | null): string {
  return `${SECTION_MARK}${text}${SECTION_MARK}${uid ?? Date.now()}`;
}

// ── Inline rename input ─────────────────────────────────────────────────────────

function RenameInput({ initial, onCommit, onCancel }: {
  initial: string; onCommit: (v: string) => void; onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <input ref={ref} value={v} onChange={e => setV(e.target.value)}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        if (e.key === "Enter") { e.stopPropagation(); onCommit(v.trim() || initial); }
        if (e.key === "Escape") { e.stopPropagation(); onCancel(); }
      }}
      onBlur={() => onCommit(v.trim() || initial)}
      style={{ flex: 1, fontSize: 13, padding: "1px 4px", border: "1px solid var(--p-400,#818cf8)", borderRadius: 3, outline: "none", background: "var(--bg-0)", color: "var(--text-1)", fontFamily: "inherit" }} />
  );
}

// ── Sub-folder leaf row ─────────────────────────────────────────────────────────

function SubFolderRow({ folder, active, canManage, onSelect, onRename, onDelete, onFileDrop }: {
  folder: DriveFolder; active: boolean; canManage: boolean;
  onSelect: () => void; onRename: (name: string) => void; onDelete: () => void;
  onFileDrop: (fileIds: string[], folderId: number) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);

  const items: CtxItem[] = canManage ? [
    { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(true) },
    { sep: true }, { label: "삭제", icon: "🗑️", onClick: onDelete, danger: true },
  ] : [];

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => { setHovered(false); setDropOver(false); }}
      onContextMenu={e => { if (canManage) { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); } }}
      onDragOver={e => { e.preventDefault(); setDropOver(true); }}
      onDragLeave={() => setDropOver(false)}
      onDrop={e => {
        e.preventDefault(); setDropOver(false);
        const raw = e.dataTransfer.getData("application/x-drive-files");
        if (raw) { try { onFileDrop(JSON.parse(raw), folder.id); } catch {} }
      }}
      style={{ display: "flex", alignItems: "center", margin: "1px 6px 1px 0", paddingLeft: 30, borderRadius: 6,
        background: dropOver ? "rgba(99,102,241,0.18)" : active ? "rgba(99,102,241,0.12)" : hovered ? "rgba(99,102,241,0.05)" : "transparent",
        outline: dropOver ? "2px dashed var(--p-400,#818cf8)" : undefined, outlineOffset: -1 }}>
      <button onClick={onSelect} style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: "5px 4px", textAlign: "left", minWidth: 0 }}>
        <span style={{ fontSize: 11, color: "var(--text-3)", flexShrink: 0 }}>＃</span>
        {renaming ? (
          <RenameInput initial={folder.name} onCommit={n => { setRenaming(false); if (n !== folder.name) onRename(n); }} onCancel={() => setRenaming(false)} />
        ) : (
          <span style={{ flex: 1, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: active ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: active ? 600 : 400 }}>{folder.name}</span>
        )}
      </button>
      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={items} onClose={() => setCtx(null)} />}
    </div>
  );
}

// ── Root folder row (also renders section dividers) ─────────────────────────────

function FolderRow({
  folder, active, canManage, expanded, activeSubId, subFolders, highlight,
  onToggle, onSelect, onRename, onDelete, onAddSub, onAddDivider, onMoveUp, onMoveDown,
  onSubSelect, onSubRename, onSubDelete,
  onDragStart, onDragOver, onDrop, onDragEnd, onFileDrop,
}: {
  folder: DriveFolder; active: boolean; canManage: boolean; expanded: boolean;
  activeSubId: number | null; subFolders: DriveFolder[]; highlight: boolean;
  onToggle: () => void; onSelect: () => void;
  onRename: (name: string) => void; onDelete: () => void;
  onAddSub: () => void; onAddDivider: () => void;
  onMoveUp: () => void; onMoveDown: () => void;
  onSubSelect: (f: DriveFolder) => void;
  onSubRename: (f: DriveFolder, name: string) => void;
  onSubDelete: (f: DriveFolder) => void;
  onDragStart: () => void; onDragOver: () => void;
  onDrop: (e: React.DragEvent) => void; onDragEnd: () => void;
  onFileDrop?: (fileIds: string[], folderId: number) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);
  const [dropFiles, setDropFiles] = useState(false);

  // ── Divider variant ──────────────────────────────────────────────────────────
  if (isDivider(folder)) {
    const sec = parseSection(folder.name);
    return (
      <div onContextMenu={e => { if (canManage) { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); } }}
        draggable={canManage && !renaming}
        onDragStart={e => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(folder.id)); onDragStart(); }}
        onDragOver={e => { e.preventDefault(); onDragOver(); }} onDrop={e => { e.preventDefault(); onDrop(e); }} onDragEnd={onDragEnd}
        onDoubleClick={() => { if (canManage) setRenaming(true); }}
        title={canManage ? "더블클릭 또는 우클릭으로 섹션 이름 변경" : undefined}
        style={{ padding: "9px 12px 4px", cursor: canManage ? "grab" : "default" }}>
        {renaming ? (
          <RenameInput initial={sec.text} onCommit={n => { setRenaming(false); onRename(n); }} onCancel={() => setRenaming(false)} />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {sec.text && <span style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text-3)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0 }}>{sec.text}</span>}
            <div style={{ flex: 1, height: 1, background: highlight ? "var(--p-500,#6366f1)" : "var(--border)" }} />
          </div>
        )}
        {ctx && <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} items={[
          { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(true) },
          { sep: true },
          { label: "삭제", icon: "🗑️", onClick: onDelete, danger: true },
        ]} />}
      </div>
    );
  }

  // ── Normal folder variant ────────────────────────────────────────────────────
  const items: CtxItem[] = canManage ? [
    { label: "하위 폴더 추가", icon: "＃", onClick: onAddSub },
    { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(true) },
    { sep: true },
    { label: "위로 이동", icon: "↑", onClick: onMoveUp },
    { label: "아래로 이동", icon: "↓", onClick: onMoveDown },
    { sep: true },
    { label: "섹션 추가", icon: "―", onClick: onAddDivider },
    { sep: true },
    { label: "삭제", icon: "🗑️", onClick: onDelete, danger: true },
  ] : [];

  return (
    <div>
      <div
        draggable={canManage && !renaming}
        onDragStart={e => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(folder.id)); onDragStart(); }}
        onDragOver={e => { e.preventDefault(); if (e.dataTransfer.types.includes("application/x-drive-files")) { setDropFiles(true); } else { onDragOver(); } }}
        onDragLeave={() => setDropFiles(false)}
        onDrop={e => { e.preventDefault(); setDropFiles(false); const raw = e.dataTransfer.getData("application/x-drive-files"); if (raw && onFileDrop) { try { onFileDrop(JSON.parse(raw), folder.id); } catch {} } else { onDrop(e); } }}
        onDragEnd={() => { setDropFiles(false); onDragEnd(); }}
        onContextMenu={e => { if (canManage) { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); } }}
        style={{ display: "flex", alignItems: "center", margin: "1px 6px 1px 0", borderRadius: 6,
          background: dropFiles ? "rgba(99,102,241,0.18)" : highlight ? "rgba(99,102,241,0.2)" : active ? "rgba(99,102,241,0.12)" : "transparent",
          outline: dropFiles ? "2px dashed var(--p-400,#818cf8)" : highlight ? "1.5px dashed var(--p-500,#6366f1)" : "none",
          outlineOffset: -1,
          cursor: canManage && !renaming ? "grab" : "pointer" }}
        onMouseEnter={e => { if (!active && !highlight) (e.currentTarget as HTMLElement).style.background = "rgba(99,102,241,0.05)"; }}
        onMouseLeave={e => { if (!active && !highlight) (e.currentTarget as HTMLElement).style.background = "transparent"; }}>
        <button onClick={e => { e.stopPropagation(); onToggle(); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 2px", color: "var(--text-3)", flexShrink: 0, display: "flex", alignItems: "center" }}>
          {subFolders.length > 0 ? (expanded ? <I.ChevD size={11} /> : <I.ChevR size={11} />) : <span style={{ width: 11, display: "inline-block" }} />}
        </button>
        <button onClick={onSelect} style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: "5px 4px", textAlign: "left", minWidth: 0 }}>
          <span style={{ fontSize: 13, flexShrink: 0 }}>📁</span>
          {renaming ? (
            <RenameInput initial={folder.name} onCommit={n => { setRenaming(false); if (n !== folder.name) onRename(n); }} onCancel={() => setRenaming(false)} />
          ) : (
            <span style={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: active ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: active ? 600 : 400 }}>{folder.name}</span>
          )}
        </button>
      </div>

      {expanded && subFolders.map(sf => (
        <SubFolderRow key={sf.id} folder={sf}
          active={activeSubId === sf.id} canManage={canManage}
          onSelect={() => onSubSelect(sf)}
          onRename={n => onSubRename(sf, n)}
          onDelete={() => onSubDelete(sf)}
          onFileDrop={onFileDrop ?? (() => {})} />
      ))}

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={items} onClose={() => setCtx(null)} />}
    </div>
  );
}

// ── Main sidebar ────────────────────────────────────────────────────────────────

export function DriveSidebar({ onCollapse, basePath = "/drive" }: { onCollapse: () => void; basePath?: string }) {
  const nav = useNavigate();
  const [params] = useSearchParams();

  const isExecDocs = basePath === "/exec/docs";
  const rawScope = isExecDocs ? "secret" : (params.get("filter") ?? "company");
  const scope: ScopeId = (SCOPES.some(s => s.id === rawScope) ? rawScope : "company") as ScopeId;
  const activeFolderId = params.get("folder") ? Number(params.get("folder")) : null;
  const activeSubId = params.get("sub") ? Number(params.get("sub")) : null;

  const isSuperAdmin = useAuth(s => s.hasRole("SUPER_ADMIN"));
  const isManager = useAuth(s => s.hasRole("DIVISION_HEAD") || s.hasRole("TEAM_LEAD"));
  const isCLevel = useAuth(s => s.hasRole("C_LEVEL") || s.hasRole("CEO"));
  const canManage = isExecDocs
    ? (isSuperAdmin || isCLevel)
    : (isSuperAdmin || scope === "personal" || (scope === "department" && isManager));

  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [subsByParent, setSubsByParent] = useState<Record<number, DriveFolder[]>>({});
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const newRef = useRef<HTMLInputElement>(null);
  const dragId = useRef<number | null>(null);
  const [dropId, setDropId] = useState<number | null>(null);
  const [bgCtx, setBgCtx] = useState<{ x: number; y: number } | null>(null);

  const scopeApi = isExecDocs ? "SECRET" : (SCOPES.find(s => s.id === scope)?.api ?? "COMPANY");

  const loadFolders = () =>
    api.get<DriveFolder[]>(`/drive/folders?scope=${scopeApi}&root_only=true`)
      .then(r => setFolders(r.data)).catch(() => {});

  const loadSubs = (parentId: number) =>
    api.get<DriveFolder[]>(`/drive/folders?parent_id=${parentId}`)
      .then(r => { setSubsByParent(p => ({ ...p, [parentId]: r.data })); return r.data; })
      .catch(() => [] as DriveFolder[]);

  useEffect(() => {
    setFolders([]);
    setSubsByParent({});
    setExpandedId(null);
    loadFolders();
  }, [scope]);

  useEffect(() => {
    if (activeFolderId) {
      setExpandedId(activeFolderId);
      if (!subsByParent[activeFolderId]) loadSubs(activeFolderId);
    }
  }, [activeFolderId]);

  useEffect(() => {
    const clear = () => setDropId(null);
    document.addEventListener("dragend", clear);
    document.addEventListener("drop", clear);
    return () => { document.removeEventListener("dragend", clear); document.removeEventListener("drop", clear); };
  }, []);

  const toggle = (folderId: number) => {
    setExpandedId(prev => (prev === folderId ? null : folderId));
    if (!subsByParent[folderId]) loadSubs(folderId);
  };

  // URL filter 값 — 내부문서는 SCOPES에 없는 "secret"을 그대로 써야 한다
  // (scope 변수는 company로 폴백되므로 URL에 쓰면 ExecDocsPage 교정 redirect와 충돌).
  const urlFilter = isExecDocs ? "secret" : scope;
  const goRoot   = () => nav(`${basePath}?filter=${urlFilter}`);
  const goFolder = (id: number) => nav(`${basePath}?filter=${urlFilter}&folder=${id}`);
  const goSub    = (parentId: number, subId: number) => nav(`${basePath}?filter=${urlFilter}&folder=${parentId}&sub=${subId}`);
  const goScope  = (s: ScopeId) => nav(`${basePath}?filter=${s}`);

  // ── Folder CRUD ─────────────────────────────────────────────────────────────
  const addFolder = async (name: string) => {
    const v = name.trim(); if (!v) return;
    try {
      await api.post("/drive/folders", { name: v, scope: scopeApi });
      loadFolders();
    } catch (e: any) { toast.error("폴더 추가 실패", e?.response?.data?.detail ?? ""); }
  };

  const addSection = async () => {
    const raw = prompt("섹션 이름 (비워두면 선만 표시)");
    if (raw === null) return;
    try {
      await api.post("/drive/folders", { name: buildSectionName(raw.trim()), scope: scopeApi });
      loadFolders();
    } catch { toast.error("섹션 추가 실패"); }
  };

  const renameFolder = async (f: DriveFolder, name: string) => {
    // For dividers, `name` is the section text — wrap it back in the sentinel
    const stored = isDivider(f) ? buildSectionName(name, parseSection(f.name).uid) : name;
    try {
      await api.patch(`/drive/folders/${f.id}`, { name: stored });
      if (f.parent_id != null) loadSubs(f.parent_id); else loadFolders();
    } catch { toast.error("이름 변경 실패"); }
  };

  const deleteFolder = async (f: DriveFolder) => {
    const label = isDivider(f) ? "이 구분선" : `"${f.name}" 폴더`;
    if (!isDivider(f) && !confirm(`${label}를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/drive/folders/${f.id}`);
      if (f.parent_id != null) loadSubs(f.parent_id);
      else { loadFolders(); if (activeFolderId === f.id) nav(`${basePath}?filter=${urlFilter}`); }
    } catch { toast.error("삭제 실패"); }
  };

  const addSubFolder = async (parentId: number) => {
    const name = prompt("하위 폴더 이름")?.trim();
    if (!name) return;
    try {
      await api.post("/drive/folders", { name, scope: scopeApi, parent_id: parentId });
      loadSubs(parentId);
      setExpandedId(parentId);
    } catch (e: any) { toast.error("하위 폴더 추가 실패", e?.response?.data?.detail ?? ""); }
  };

  const moveFiles = async (fileIds: string[], folderId: number) => {
    const numericIds = fileIds.map(id => Number(id)).filter(n => !isNaN(n));
    for (const id of numericIds) {
      try { await api.patch(`/drive/files/${id}`, { folder_id: folderId }); } catch {}
    }
    window.dispatchEvent(new CustomEvent("drive:refresh"));
  };

  const reorderFolders = async (ids: number[]) => {
    setFolders(ids.map(id => folders.find(f => f.id === id)!).filter(Boolean));
    try { await api.put("/drive/folders/reorder", { ids }); }
    catch { toast.error("순서 변경 실패"); loadFolders(); }
  };

  const moveFolderByOne = (f: DriveFolder, dir: -1 | 1) => {
    const ids = folders.map(x => x.id);
    const i = ids.indexOf(f.id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorderFolders(ids);
  };

  const handleFolderDrop = (_e: React.DragEvent, target: DriveFolder) => {
    setDropId(null);
    const from = dragId.current; dragId.current = null;
    if (from == null || from === target.id) return;
    const ids = folders.map(f => f.id);
    const fi = ids.indexOf(from), ti = ids.indexOf(target.id);
    if (fi < 0 || ti < 0) return;
    ids.splice(ti, 0, ids.splice(fi, 1)[0]);
    reorderFolders(ids);
  };

  const isDashboard = !activeFolderId;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "14px 12px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>{basePath === "/exec/docs" ? "내부문서" : "드라이브"}</h3>
          <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--text-3)" }}>파일 · 버전 관리</p>
        </div>
        <button onClick={onCollapse} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: 2, borderRadius: 4 }} title="접기">
          <I.PanelL size={14} />
        </button>
      </div>

      {/* Scope pill tabs — 내부문서에서는 숨김 (항상 SECRET 스코프) */}
      {!isExecDocs && (
        <div style={{ display: "flex", gap: 4, padding: "8px 10px 4px", flexShrink: 0 }}>
          {SCOPES.map(s => (
            <button key={s.id} onClick={() => goScope(s.id)}
              style={{ flex: 1, fontSize: 11, fontWeight: scope === s.id ? 700 : 400, padding: "4px 6px", border: "none", borderRadius: 5, cursor: "pointer",
                background: scope === s.id ? "var(--p-500,#6366f1)" : "var(--bg-1,rgba(0,0,0,0.04))",
                color: scope === s.id ? "#fff" : "var(--text-2)" }}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Dashboard shortcut */}
      <div style={{ padding: "4px 8px 2px", flexShrink: 0 }}>
        <button onClick={goRoot}
          style={{ display: "flex", alignItems: "center", gap: 7, width: "100%", border: "none", cursor: "pointer", textAlign: "left", padding: "6px 10px", borderRadius: 6, fontSize: 13,
            background: isDashboard ? "rgba(99,102,241,0.12)" : "transparent",
            color: isDashboard ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: isDashboard ? 600 : 400 }}>
          <span>📊</span><span>대시보드</span>
        </button>
      </div>

      {/* Folder tree */}
      <div style={{ flex: 1, overflowY: "auto", padding: "2px 4px" }}
        onContextMenu={e => { if (canManage) { e.preventDefault(); setBgCtx({ x: e.clientX, y: e.clientY }); } }}>
        <div style={{ display: "flex", alignItems: "center", padding: "6px 8px 2px 10px" }}>
          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>폴더</span>
          {canManage && (
            <button title="폴더 추가" onClick={() => { setAdding(true); setTimeout(() => newRef.current?.focus(), 0); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: "2px 4px", borderRadius: 3, display: "flex", alignItems: "center" }}>
              <I.Plus size={12} />
            </button>
          )}
        </div>

        {folders.map(f => (
          <FolderRow key={f.id} folder={f} canManage={canManage}
            active={activeFolderId === f.id} expanded={expandedId === f.id}
            activeSubId={activeSubId} subFolders={subsByParent[f.id] ?? []}
            highlight={dropId === f.id}
            onToggle={() => toggle(f.id)} onSelect={() => goFolder(f.id)}
            onRename={n => renameFolder(f, n)} onDelete={() => deleteFolder(f)}
            onAddSub={() => addSubFolder(f.id)} onAddDivider={addSection}
            onMoveUp={() => moveFolderByOne(f, -1)} onMoveDown={() => moveFolderByOne(f, 1)}
            onSubSelect={sf => goSub(f.id, sf.id)}
            onSubRename={(sf, n) => renameFolder(sf, n)}
            onSubDelete={sf => deleteFolder(sf)}
            onDragStart={() => { dragId.current = f.id; }}
            onDragOver={() => setDropId(f.id)}
            onDrop={e => handleFolderDrop(e, f)}
            onDragEnd={() => { dragId.current = null; setDropId(null); }}
            onFileDrop={moveFiles} />
        ))}

        {adding && (
          <div style={{ display: "flex", gap: 4, margin: "2px 6px", padding: "4px 8px", alignItems: "center", background: "rgba(99,102,241,0.07)", borderRadius: 6, border: "1px dashed var(--p-400,#818cf8)" }}>
            <span style={{ fontSize: 13 }}>📁</span>
            <input ref={newRef} value={newLabel} placeholder="폴더 이름…" onChange={e => setNewLabel(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") { addFolder(newLabel); setNewLabel(""); setAdding(false); }
                if (e.key === "Escape") { setAdding(false); setNewLabel(""); }
              }}
              onBlur={() => { if (newLabel.trim()) addFolder(newLabel); setNewLabel(""); setAdding(false); }}
              style={{ flex: 1, fontSize: 12, border: "none", outline: "none", background: "transparent", color: "var(--text-1)", fontFamily: "inherit" }} />
          </div>
        )}
      </div>

      {bgCtx && (
        <ContextMenu x={bgCtx.x} y={bgCtx.y} onClose={() => setBgCtx(null)} items={[
          { label: "폴더 추가", icon: "📁", onClick: () => { setAdding(true); setTimeout(() => newRef.current?.focus(), 0); } },
          { label: "섹션 추가", icon: "―", onClick: addSection },
        ]} />
      )}
    </div>
  );
}
